import { useQuery } from "@tanstack/react-query";
import { AlertCircle, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { fetchFoodDashboard } from "../foodApi";
import { money, pct, shortDate } from "../utils/foodFormatters";
import { QueryError } from "../components/QueryError";

const STAT_LABELS = ["Ingredients", "Menu Items", "Invoices", "Items Over Target"] as const;

export function DashboardView() {
  const q = useQuery({ queryKey: ["food", "dashboard"], queryFn: fetchFoodDashboard });

  if (q.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-sm text-tribal-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading dashboard...
      </div>
    );
  }
  if (q.isError || !q.data) {
    return <QueryError message={`Failed to load dashboard: ${(q.error as Error).message}`} />;
  }

  const stats = [
    { label: STAT_LABELS[0], value: q.data.totalIngredients },
    { label: STAT_LABELS[1], value: q.data.totalMenuItems },
    { label: STAT_LABELS[2], value: q.data.totalInvoices },
    { label: STAT_LABELS[3], value: q.data.menuItemsOverTarget },
  ];

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {stats.map((s) => (
          <div key={s.label} className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
            <div className="text-xs uppercase tracking-wide text-tribal-muted">{s.label}</div>
            <div className="mt-1 text-2xl font-semibold text-white">{s.value}</div>
          </div>
        ))}
      </div>

      {(q.data.menuItemsIncompleteCost ?? 0) > 0 && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-5 py-3 text-sm text-amber-300">
          {q.data.menuItemsIncompleteCost} menu item(s) have an ingredient with no usable cost. They
          are left out of the average and over-target count until fixed.{" "}
          <Link to="/food/analysis" className="underline">
            See which
          </Link>
        </div>
      )}

      {q.data.averageFoodCostPct !== null && (
        <div className="flex items-center justify-between rounded-lg border border-tribal-border bg-tribal-surface px-5 py-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-tribal-muted">Average Food Cost %</div>
            <div className="mt-1 text-2xl font-bold text-white">{pct(q.data.averageFoodCostPct)}</div>
          </div>
          <Link
            to="/food/analysis"
            className="rounded-md border border-tribal-border px-3 py-1.5 text-sm font-medium text-tribal-muted transition-colors hover:bg-tribal-border hover:text-white"
          >
            View Full Analysis
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-tribal-border bg-tribal-surface">
          <div className="border-b border-tribal-border px-4 py-3 text-sm font-semibold text-white">
            Recent Invoices
          </div>
          <div className="p-3">
            {q.data.recentInvoices.length === 0 ? (
              <p className="py-4 text-center text-sm text-tribal-muted">No invoices yet</p>
            ) : (
              <div className="space-y-1">
                {q.data.recentInvoices.map((inv) => (
                  <Link
                    key={inv.id}
                    to={`/food/invoices/${inv.id}`}
                    className="flex items-center justify-between rounded-md px-3 py-2 transition-colors hover:bg-white/5"
                  >
                    <div>
                      <p className="text-sm font-medium text-white">{inv.vendorName}</p>
                      <p className="text-xs text-tribal-muted">
                        {shortDate(inv.invoiceDate)}
                        {inv.invoiceNumber && ` \u2022 #${inv.invoiceNumber}`}
                      </p>
                    </div>
                    {inv.totalAmount != null && (
                      <span className="text-sm font-medium text-white">{money(inv.totalAmount)}</span>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-tribal-border bg-tribal-surface">
          <div className="flex items-center gap-2 border-b border-tribal-border px-4 py-3 text-sm font-semibold text-white">
            <AlertCircle className="h-4 w-4 text-yellow-500" />
            Price Spike Alerts
          </div>
          <div className="p-3">
            {q.data.priceAlerts.length === 0 ? (
              <p className="py-4 text-center text-sm text-tribal-muted">
                No significant price increases detected
              </p>
            ) : (
              <div className="space-y-1">
                {q.data.priceAlerts.map((a) => (
                  <Link
                    key={a.ingredientId}
                    to={`/food/ingredients/${a.ingredientId}`}
                    className="flex items-center justify-between rounded-md px-3 py-2 transition-colors hover:bg-white/5"
                  >
                    <div>
                      <p className="text-sm font-medium text-white">{a.ingredientName}</p>
                      <p className="text-xs text-tribal-muted">
                        {money(a.previousCost)} avg &rarr; {money(a.currentCost)} latest
                      </p>
                    </div>
                    <span className="rounded bg-red-500/20 px-2 py-0.5 text-xs font-semibold text-red-400">
                      +{pct(a.changePercent)}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-tribal-border bg-tribal-surface">
        <div className="border-b border-tribal-border px-4 py-3 text-sm font-semibold text-white">
          Quick Actions
        </div>
        <div className="flex flex-wrap gap-3 p-4">
          <Link
            to="/food/upload"
            className="inline-flex items-center gap-2 rounded-md bg-tribal-red px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-tribal-red/80"
          >
            Upload Invoice
          </Link>
          <Link
            to="/food/ingredients"
            className="inline-flex items-center gap-2 rounded-md border border-tribal-border px-4 py-2 text-sm font-medium text-tribal-muted transition-colors hover:bg-tribal-border hover:text-white"
          >
            Add Ingredient
          </Link>
          <Link
            to="/food/menu"
            className="inline-flex items-center gap-2 rounded-md border border-tribal-border px-4 py-2 text-sm font-medium text-tribal-muted transition-colors hover:bg-tribal-border hover:text-white"
          >
            Add Menu Item
          </Link>
          <Link
            to="/food/analysis"
            className="inline-flex items-center gap-2 rounded-md border border-tribal-border px-4 py-2 text-sm font-medium text-tribal-muted transition-colors hover:bg-tribal-border hover:text-white"
          >
            View Analysis
          </Link>
        </div>
      </div>
    </div>
  );
}
